import type { PDFDocumentProxy } from "pdfjs-dist/types/src/display/api"
import {
  memo,
  useEffect,
  type Dispatch,
  type FC,
  type MutableRefObject,
  type ReactNode,
  type SetStateAction,
} from "react"
import type {
  IHighlight,
  PdfHighlighterInstance,
  Position,
  ScaledPosition,
  ScrollToHighlight,
} from "react-pdf-highlighter"
import { Highlight, PdfHighlighter } from "react-pdf-highlighter"

type PdfDocumentLayerProps = {
  pdfDocument: PDFDocumentProxy
  scale: number
  highlights: IHighlight[]
  pdfHighlighterRef: MutableRefObject<PdfHighlighterInstance | null>
  scrollViewerToRef: MutableRefObject<ScrollToHighlight | null>
  setTotalPages: Dispatch<SetStateAction<number>>
  totalPages: number
  pdfDocumentRef: MutableRefObject<PDFDocumentProxy | null>
  onViewerPageChange: (page: number) => void
  initialPage: number
  pendingPageRef: MutableRefObject<number | null>
  onTextSelected?: (page: number, text: string) => void
}

export const PdfDocumentLayer: FC<PdfDocumentLayerProps> = memo(
  ({
    pdfDocument,
    scale,
    highlights,
    pdfHighlighterRef,
    scrollViewerToRef,
    setTotalPages,
    totalPages,
    pdfDocumentRef,
    onViewerPageChange,
    initialPage,
    pendingPageRef,
    onTextSelected,
  }) => {
    useEffect(() => {
      pdfDocumentRef.current = pdfDocument
    }, [pdfDocument, pdfDocumentRef])

    useEffect(() => {
      if (pdfDocument?.numPages && pdfDocument.numPages !== totalPages) {
        setTotalPages(pdfDocument.numPages)
      }
    }, [pdfDocument, totalPages, setTotalPages])

    // Navigate to initial page after PDF pages are loaded
    useEffect(() => {
      let detachPagesLoaded: (() => void) | null = null
      let rafId: number | null = null

      const setupInitialNavigation = () => {
        const viewer = pdfHighlighterRef.current?.viewer
        const eventBus = viewer?.eventBus
        if (!eventBus?.on || !viewer) {
          rafId = window.requestAnimationFrame(setupInitialNavigation)
          return
        }

        const handlePagesLoaded = () => {
          // Wait a frame for viewer to stabilize
          setTimeout(() => {
            const currentViewer = pdfHighlighterRef.current?.viewer
            if (!currentViewer || !pdfDocument?.numPages) {
              return
            }

            // Validate page is within bounds
            if (initialPage > 0 && initialPage <= pdfDocument.numPages) {
              if (currentViewer.currentPageNumber !== initialPage) {
                currentViewer.currentPageNumber = initialPage
              }
              if (pendingPageRef.current === initialPage) {
                pendingPageRef.current = null
              }
            }
          }, 100)
        }

        // Listen for pagesloaded event
        eventBus.on("pagesloaded", handlePagesLoaded)
        detachPagesLoaded = () =>
          eventBus.off?.("pagesloaded", handlePagesLoaded)
      }

      setupInitialNavigation()

      return () => {
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId)
        }
        detachPagesLoaded?.()
      }
    }, [pdfDocument, initialPage, pdfHighlighterRef, pendingPageRef])

    useEffect(() => {
      let detach: (() => void) | null = null
      let rafId: number | null = null

      const attach = () => {
        const viewer = pdfHighlighterRef.current?.viewer
        const eventBus = viewer?.eventBus
        if (!eventBus?.on) {
          rafId = window.requestAnimationFrame(attach)
          return
        }

        const handlePageChange = (event: { pageNumber?: number }) => {
          if (typeof event?.pageNumber === "number") {
            onViewerPageChange(event.pageNumber)
          }
        }

        eventBus.on("pagechanging", handlePageChange)
        detach = () => eventBus.off?.("pagechanging", handlePageChange)
      }

      attach()

      return () => {
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId)
        }
        detach?.()
      }
    }, [onViewerPageChange, pdfDocument, pdfHighlighterRef])

    return (
      <PdfHighlighter
        ref={pdfHighlighterRef}
        pdfDocument={pdfDocument}
        pdfScaleValue={scale.toString()}
        enableAreaSelection={() => false}
        scrollRef={(scrollTo: ScrollToHighlight) => {
          scrollViewerToRef.current = scrollTo
        }}
        onSelectionFinished={(position, content, hideTipAndSelection) => {
          const text = content.text?.trim()
          if (text && onTextSelected) {
            onTextSelected(position.pageNumber, text)
            hideTipAndSelection()
          }
          return null
        }}
        highlightTransform={(
          highlight: IHighlight,
          index: number,
          _setTip: (
            highlight: IHighlight,
            renderTooltip: () => ReactNode,
          ) => void,
          _hideTip: () => void,
          _viewportToScaled: (position: Position) => ScaledPosition,
          _screenshot: string | null,
          isScrolledTo: boolean,
        ) => (
          <Highlight
            key={index}
            isScrolledTo={isScrolledTo}
            position={highlight.position}
            comment={highlight.comment}
          />
        )}
        highlights={highlights}
      />
    )
  },
  (prevProps, nextProps) =>
    prevProps.pdfDocument === nextProps.pdfDocument &&
    prevProps.scale === nextProps.scale &&
    prevProps.highlights === nextProps.highlights &&
    prevProps.totalPages === nextProps.totalPages &&
    prevProps.initialPage === nextProps.initialPage &&
    prevProps.onTextSelected === nextProps.onTextSelected,
)

PdfDocumentLayer.displayName = "PdfDocumentLayer"
